import React, { useCallback, useEffect, useState } from "react";
import { briefingDispatcherApi } from "@/api/oraclePhase28Client";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — AdminOne Daily Briefing Dispatcher Panel (Phase 28C/D).
 *
 * Shows:
 *   • Cron schedule + provider status (Configured / Awaiting Provider Key)
 *   • Manual "Dispatch Now" buttons (SMS / Email / Both)
 *   • Dispatch history
 *   • PDF preview / download
 */

const STATUS_COLORS = {
  sent:                   "#10b981",
  configured:             "#10b981",
  awaiting_provider_key:  "#fcd34d",
  queued:                 "#fcd34d",
  failed:                 "#ef4444",
};

function StatusPill({ status, label, testid }) {
  const c = STATUS_COLORS[status] || "#94a3b8";
  return (
    <span
      style={{
        fontFamily: "'Space Mono', monospace",
        fontSize: 10.5,
        letterSpacing: "0.08em",
        fontWeight: 800,
        padding: "3px 10px",
        border: `1px solid ${c}`,
        borderRadius: 999,
        color: c,
        background: `${c}1a`,
      }}
      data-testid={testid}
    >
      {(label || status || "").toUpperCase()}
    </span>
  );
}

export default function AdminBriefingDispatcher({ testid = "admin-briefing-dispatcher" }) {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dispatching, setDispatching] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [s, h] = await Promise.all([
        briefingDispatcherApi.status(),
        briefingDispatcherApi.history(15),
      ]);
      setStatus(s.data);
      setHistory(h.data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dispatch = useCallback(async (channel) => {
    setDispatching(channel); setFeedback(null);
    try {
      const { data } = await briefingDispatcherApi.dispatchNow(channel);
      setFeedback(data);
      // Refresh history afterwards.
      const h = await briefingDispatcherApi.history(15);
      setHistory(h.data?.items || []);
    } catch (e) {
      setFeedback({ error: e?.response?.data?.detail || e?.message || "dispatch_failed" });
    } finally { setDispatching(""); }
  }, []);

  const downloadPdf = useCallback(async () => {
    setDispatching("pdf");
    try {
      const blob = await briefingDispatcherApi.fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CareLogix-ORACLE-CEO-Briefing-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setFeedback({ error: e?.response?.data?.detail || e?.message || "pdf_failed" });
    } finally { setDispatching(""); }
  }, []);

  return (
    <section style={S.shell} data-testid={testid}>
      <ComplianceLock variant="banner" policy="v27" testid={`${testid}-compliance-top`} />

      <header style={S.head}>
        <div>
          <span style={S.tagline}>Daily Briefing Dispatcher · 8AM SMS / 9AM Email · PDF</span>
          <h3 style={S.title}>CEO Briefing Pipeline</h3>
        </div>
        <button type="button" onClick={load} disabled={busy} style={S.refresh} data-testid={`${testid}-refresh`}>
          {busy ? "…" : "↻ Refresh"}
        </button>
      </header>

      {err && <div style={S.err} data-testid={`${testid}-error`}>⚠ {err}</div>}

      <div style={S.grid}>
        <div style={S.card} data-testid={`${testid}-sms-card`}>
          <div style={S.cardHead}>
            <span style={S.cardIcon} aria-hidden="true">📲</span>
            <strong style={S.cardTitle}>SMS · 08:00 UTC</strong>
          </div>
          {status?.sms && (
            <>
              <div style={S.cardRow}><span style={S.cardLbl}>RECIPIENT</span><code style={S.cardVal}>{status.sms.recipient}</code></div>
              <div style={S.cardRow}><span style={S.cardLbl}>CRON</span><code style={S.cardVal}>{status.sms.cron}</code></div>
              <div style={S.cardRow}>
                <span style={S.cardLbl}>STATUS</span>
                <StatusPill
                  status={status.sms.configured ? "configured" : "awaiting_provider_key"}
                  label={status.sms.status_label}
                  testid={`${testid}-sms-status`}
                />
              </div>
            </>
          )}
          <button
            type="button"
            onClick={() => dispatch("sms")}
            disabled={dispatching !== ""}
            style={S.primary}
            data-testid={`${testid}-dispatch-sms`}
          >
            {dispatching === "sms" ? "Dispatching…" : "Dispatch SMS Now"}
          </button>
        </div>

        <div style={S.card} data-testid={`${testid}-email-card`}>
          <div style={S.cardHead}>
            <span style={S.cardIcon} aria-hidden="true">📧</span>
            <strong style={S.cardTitle}>Email + PDF · 09:00 UTC</strong>
          </div>
          {status?.email && (
            <>
              <div style={S.cardRow}><span style={S.cardLbl}>RECIPIENT</span><code style={S.cardVal}>{status.email.recipient}</code></div>
              <div style={S.cardRow}><span style={S.cardLbl}>SUBJECT</span><code style={{ ...S.cardVal, fontSize: 10.5 }}>{status.email.subject}</code></div>
              <div style={S.cardRow}><span style={S.cardLbl}>CRON</span><code style={S.cardVal}>{status.email.cron}</code></div>
              <div style={S.cardRow}>
                <span style={S.cardLbl}>STATUS</span>
                <StatusPill
                  status={status.email.configured ? "configured" : "awaiting_provider_key"}
                  label={status.email.status_label}
                  testid={`${testid}-email-status`}
                />
              </div>
            </>
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => dispatch("email")}
              disabled={dispatching !== ""}
              style={S.primary}
              data-testid={`${testid}-dispatch-email`}
            >
              {dispatching === "email" ? "Dispatching…" : "Dispatch Email Now"}
            </button>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={dispatching !== ""}
              style={S.ghost}
              data-testid={`${testid}-pdf-download`}
            >
              {dispatching === "pdf" ? "Building…" : "⬇ Download PDF"}
            </button>
          </div>
        </div>

        <div style={S.card} data-testid={`${testid}-both-card`}>
          <div style={S.cardHead}>
            <span style={S.cardIcon} aria-hidden="true">⚡</span>
            <strong style={S.cardTitle}>Dispatch Both</strong>
          </div>
          <p style={S.note}>
            Fires the 8AM SMS and 9AM Email pipelines back-to-back. If provider keys are missing,
            the message is honestly queued in the dispatch log instead of silently failing.
          </p>
          <button
            type="button"
            onClick={() => dispatch("both")}
            disabled={dispatching !== ""}
            style={S.primary}
            data-testid={`${testid}-dispatch-both`}
          >
            {dispatching === "both" ? "Dispatching…" : "Dispatch SMS + Email Now"}
          </button>
        </div>
      </div>

      {feedback && (
        <div style={S.feedback} data-testid={`${testid}-feedback`}>
          {feedback.error ? (
            <span style={{ color: "#fecaca" }}>⚠ {feedback.error}</span>
          ) : (
            <>
              <strong style={{ color: "#fde68a" }}>Dispatch result</strong>
              {feedback.sms && (
                <div style={S.feedRow}>
                  <StatusPill status={feedback.sms.status} testid={`${testid}-fb-sms`} />
                  <code style={S.feedCode}>SMS → {feedback.sms.recipient}</code>
                  <span style={S.feedNote}>{feedback.sms.reason || feedback.sms.provider_sid || ""}</span>
                </div>
              )}
              {feedback.email && (
                <div style={S.feedRow}>
                  <StatusPill status={feedback.email.status} testid={`${testid}-fb-email`} />
                  <code style={S.feedCode}>EMAIL → {feedback.email.recipient}</code>
                  <span style={S.feedNote}>{feedback.email.reason || `PDF bytes: ${feedback.email.pdf_bytes || 0}`}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div style={S.historyWrap} data-testid={`${testid}-history`}>
        <div style={S.historyHead}>
          <strong style={{ color: "#c5a059" }}>Dispatch History</strong>
          <span style={S.countChip}>{history.length} entries</span>
        </div>
        {history.length === 0 ? (
          <p style={S.empty}>No dispatches logged yet.</p>
        ) : (
          <div style={S.historyBody}>
            {history.map((h) => (
              <div key={h._id || `${h.channel}-${h.timestamp}`} style={S.historyRow} data-testid={`${testid}-history-row`}>
                <StatusPill status={h.status} />
                <code style={S.histCol}>{(h.channel || "—").toUpperCase()}</code>
                <code style={S.histCol}>{h.recipient || "—"}</code>
                <code style={{ ...S.histCol, color: "#94a3b8" }}>{(h.timestamp || h.saved_at || "").slice(0, 19).replace("T", " ")}</code>
                <span style={S.histNote}>{h.reason || h.provider_sid || h.trigger || ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const S = {
  shell: { background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.20)", borderRadius: 14, padding: 14, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 12, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 16 },
  refresh: { appearance: "none", background: "transparent", border: "1px solid rgba(197,160,89,0.45)", color: "#c5a059", padding: "5px 11px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  err: { padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 },
  card: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  cardHead: { display: "flex", alignItems: "center", gap: 8 },
  cardIcon: { fontSize: 20 },
  cardTitle: { color: "#fde68a", fontSize: 14 },
  cardRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  cardLbl: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em" },
  cardVal: { background: "rgba(255,255,255,0.04)", color: "#cbd5e1", padding: "2px 8px", borderRadius: 6, fontFamily: "'Space Mono', monospace", fontSize: 11 },
  note: { color: "#94a3b8", fontSize: 11.5, lineHeight: 1.5, margin: 0 },
  primary: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "7px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  ghost: { appearance: "none", background: "transparent", border: "1px solid rgba(197,160,89,0.45)", color: "#c5a059", padding: "7px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  feedback: { background: "#040608", border: "1px solid rgba(197,160,89,0.30)", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 6 },
  feedRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  feedCode: { background: "rgba(255,255,255,0.04)", padding: "2px 8px", borderRadius: 6, fontFamily: "'Space Mono', monospace", color: "#cbd5e1", fontSize: 11 },
  feedNote: { color: "#94a3b8", fontSize: 11, fontStyle: "italic" },
  historyWrap: { display: "flex", flexDirection: "column", gap: 6 },
  historyHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  countChip: { color: "#c5a059", border: "1px solid #c5a059", padding: "3px 9px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.10em", fontWeight: 800 },
  historyBody: { display: "flex", flexDirection: "column", gap: 4, maxHeight: 280, overflowY: "auto" },
  historyRow: { display: "grid", gridTemplateColumns: "auto 80px minmax(160px, 1fr) 150px 1.4fr", gap: 8, padding: "6px 10px", background: "#040608", border: "1px solid rgba(255,255,255,0.04)", borderRadius: 8, alignItems: "center", fontSize: 11 },
  histCol: { fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#cbd5e1" },
  histNote: { color: "#94a3b8", fontSize: 10.5, fontStyle: "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  empty: { textAlign: "center", padding: 12, color: "#94a3b8", fontFamily: "'Space Mono', monospace", fontSize: 11.5, margin: 0 },
};
